import { propertiesIcon } from "../icons.js";
import { formatDateRange } from "../utils/dates.js";
import { escapeAttr, escapeHtml } from "../utils/html.js";
import { renderEmptyState, renderViewEmptyState } from "./empty.js";
import { t } from "../../../../shared/i18n/index.js";
import { tagLabel } from "../../../../engine/core/tag-filter.js";

export function renderBoard(appState, deps) {
  const {
    callbacks,
    uiState,
    collectLeafTasks,
    getBoardColumns,
    getTagDefinitions,
    nodeHasTagId,
  } = deps;
  const root = appState.data;
  if (!root) return renderViewEmptyState(t("mobile.empty.noDocument"), t("mobile.board.noDocText"));

  const tagDefs = getTagDefinitions();
  const tagMap = new Map(tagDefs.map((tag) => [tag.id, tag]));
  const columns = getBoardColumns(tagDefs);
  const horizon = callbacks.sharedCallbacks?.getHorizonPredicate?.();
  const tasks = collectLeafTasks(root).filter((node) => !horizon || !horizon(node));

  if (!columns.length) {
    return '<div data-horizon-host class="mobile-horizon-host"></div>' + renderViewEmptyState(
      t("mobile.board.noColumns"),
      t("mobile.board.noColumnsText"),
    );
  }

  uiState.boardColumnIndex = Math.min(uiState.boardColumnIndex, columns.length - 1);
  const activeColumn = columns[uiState.boardColumnIndex];
  const activeTag = tagMap.get(activeColumn.tagId);
  const cards = tasks.filter((task) => nodeHasTagId(task, activeColumn.tagId));

  return `
    <section class="mobile-view mobile-view-padded">
      <div data-horizon-host class="mobile-horizon-host"></div>
      <div class="mobile-segmented">
        ${columns.map((col, index) => {
          const tag = tagMap.get(col.tagId);
          return `
            <button class="${index === uiState.boardColumnIndex ? "active" : ""}"
                    data-mobile-action="board-column"
                    data-column-index="${index}">
              ${escapeHtml(tagLabel(tag) || t("mobile.board.column"))}
            </button>
          `;
        }).join("")}
      </div>
      <div class="mobile-section-title">${escapeHtml(t("mobile.board.columnCount", { name: tagLabel(activeTag) || t("mobile.board.column"), n: cards.length }))}</div>
      <div class="mobile-board-list">
        ${cards.length ? cards.map((task) => renderBoardCard(task, columns, tagMap, activeColumn.tagId, callbacks, uiState)).join("") : renderEmptyState(t("mobile.board.noCards"), t("mobile.board.noCardsText"), t("mobile.fab.addCard"), "add-node-to-selected")}
      </div>
    </section>
  `;
}

function renderBoardCard(task, columns, tagMap, currentTagId, callbacks, uiState) {
  const currentTag = tagMap.get(currentTagId);
  const expanded = uiState.expandedBoardNodeId === task.id;
  return `
    <article class="mobile-board-card">
      <div class="mobile-board-title-row">
        <button class="mobile-board-title-toggle ${expanded ? "expanded" : ""}"
                data-mobile-action="toggle-board-title"
                data-node-id="${escapeAttr(task.id)}">
          ${escapeHtml(task.name || t("common.untitled"))}
        </button>
        ${callbacks.Store.isReadOnly?.() ? "" : `
          <button class="mobile-status-change-btn"
                  data-mobile-action="change-card-status"
                  data-node-id="${escapeAttr(task.id)}"
                  data-current-tag-id="${escapeAttr(currentTagId)}"
                  aria-label="${escapeAttr(t("mobile.board.changeStatus"))}">
            ${escapeHtml(tagLabel(currentTag) || t("mobile.node.status"))}
          </button>
        `}
        <button class="mobile-row-action compact"
                data-mobile-action="open-node-properties"
                data-node-id="${escapeAttr(task.id)}"
                aria-label="${escapeAttr(t("app.nodeModal.title"))}">${propertiesIcon()}</button>
      </div>
      <div class="mobile-board-meta">
        ${task.parentName ? `<span>${escapeHtml(task.parentName)}</span>` : ""}
        ${formatDateRange(task) ? `<span>${escapeHtml(formatDateRange(task))}</span>` : ""}
      </div>
    </article>
  `;
}
